import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { cn } from '../../utils/classnames';

/**
 * VerificationCodeInput Component
 * Displays a series of input boxes for entering verification codes
 */
export const VerificationCodeInput = ({ length = 6, value, onChange, error }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    // Auto-focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (element, index) => {
    const val = element.value;
    
    // Only allow numbers
    if (/^[0-9]$/.test(val)) {
      const newValue = [...value];
      newValue[index] = val;
      onChange(newValue);

      // Move to next input
      if (index < length - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (element, index, event) => {
    if (event.key === 'Backspace') {
      if (!value[index] && index > 0 && inputRefs.current[index - 1]) {
        // Move to previous input
        inputRefs.current[index - 1].focus();
      } else if (value[index]) {
        // Clear current input
        const newValue = [...value];
        newValue[index] = '';
        onChange(newValue);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.match(/\d/g);
    
    if (digits) {
      const newValue = [...value];
      digits.slice(0, length).forEach((digit, index) => {
        newValue[index] = digit;
      });
      onChange(newValue);
      
      // Focus last filled input or last input
      const lastIndex = Math.min(digits.length, length) - 1;
      if (inputRefs.current[lastIndex]) {
        inputRefs.current[lastIndex].focus();
      }
    }
  };

  return (
    <div className="flex flex-col items-center w-full gap-4">
      <div className="flex gap-3">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={value[index] || ''}
            onChange={(e) => handleChange(e.target, index)}
            onKeyDown={(e) => handleKeyDown(e.target, index, e)}
            onPaste={handlePaste}
            className={cn(
              'w-20 h-10 text-center text-2xl font-semibold',
              'bg-app-surface text-[#FEFCEB] rounded-full',
              'border-1 transition-all duration-200',
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500'
                : 'border-blue-shade-2 focus:ring-2 focus:blue-shade-2',
              'outline-none',
              'font-proxima'
            )}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-500 text-center">{error}</p>
      )}
    </div>
  );
};

VerificationCodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default VerificationCodeInput;
